import { useState } from 'react'
import { QrcodeOutlined, ReloadOutlined, ShoppingOutlined, ThunderboltOutlined, WalletOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Col,
  Descriptions,
  Flex,
  Modal,
  Row,
  Skeleton,
  Tag,
  Typography,
} from 'antd'
import { useTranslation } from 'react-i18next'
import { currentLanguage } from '@/app/i18n'
import { fetchBalance } from '@/features/dashboard/balanceApi'
import { VoucherRedeemModal } from '@/features/voucher/VoucherRedeemModal'
import {
  fetchPlans,
  isMethodMismatch,
  purchasePlan,
  purchaseShortfall,
  type Plan,
} from '@/features/plans/plansApi'
import { isInsufficientFunds, translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import type { DeliveryMethod } from '@/shared/auth/authApi'
import { useAuth } from '@/shared/auth/useAuth'
import { formatBalanceBytes, formatDuration } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import { EmptyState } from '@/shared/ui/EmptyState'
import { LoadError } from '@/shared/ui/LoadError'

/**
 * Rejaning "hajmi" — `BillingKind` ga qarab hajm (`quotaBytes`), vaqt
 * (`quotaSeconds`) yoki ikkalasi bo'lishi mumkin; ikkalasi ham `null` bo'lsa
 * cheksiz (`UNLIMITED`/`POSTPAID`).
 */
function sizeLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  plan: { quotaBytes: number | null; quotaSeconds: number | null },
): string {
  const parts: string[] = []
  if (plan.quotaBytes !== null) parts.push(formatBalanceBytes(plan.quotaBytes))
  if (plan.quotaSeconds !== null) parts.push(formatDuration(plan.quotaSeconds))
  if (parts.length === 0) return t('common.unlimited')
  return parts.join(' · ')
}

function durationLabel(t: (key: string, opts?: Record<string, unknown>) => string, validityDays: number | null): string {
  return validityDays === null ? t('common.unlimited') : t('plans.durationDays', { count: validityDays })
}

/**
 * Tezlik — MikroTik shaklidan ({@code "10M/10M"}) o'qiladigan matnga.
 *
 * ⚠️ Shakl `rx/tx` router nuqtai nazaridan: BIRINCHISI abonent yuboradigan
 * (upload), ikkinchisi abonentga keladigan (download) tezlik. Ularni
 * almashtirib qo'yish oson va oqibati jiddiy: xaridor "50 Mbit yuklab
 * olaman" deb o'ylab, aslida 5 Mbit oladi.
 *
 * ⚠️ Xom qiymat TANIB BO'LMASA — o'z holicha ko'rsatiladi, tashlab
 * yuborilmaydi. Siyosatda `"512k/256k"`, `"10M/10M 20M/20M"` (burst bilan)
 * kabi shakllar bo'lishi mumkin va ularning hammasini bu yerda qayta
 * yozishga urinish yolg'on ma'lumot berish xavfini tug'dirardi.
 */
function speedLabel(raw: string | null): string | null {
  if (!raw) return null
  const [up, down] = raw.trim().split('/')
  // Ikkala bo'lak ham bo'lmasa — xom qiymat o'z holicha
  if (!down || !up) return raw.trim()
  const pretty = (v: string) => {
    const m = /^(\d+)([kKmMgG])$/.exec(v)
    return m ? `${m[1]} ${m[2]?.toUpperCase() ?? ''}bit` : v
  }
  return `↓ ${pretty(down)} · ↑ ${pretty(up)}`
}

function PlanCard({
  plan,
  onBuy,
  buying,
  subscriberKind,
}: {
  plan: Plan
  onBuy: () => void
  buying: boolean
  subscriberKind: DeliveryMethod
}) {
  const { t } = useTranslation()
  const speed = speedLabel(plan.rateLimit)
  const mismatch = isMethodMismatch(plan, subscriberKind)

  /*
    ⚠️ KARTA QAYTA YOZILDI — ilgari unda faqat nom, hajm, muddat va narx
    bor edi. Xaridor uchun eng muhim savol ("qaysi tezlikda?") javobsiz
    qolardi: bir xil hajmdagi ikki paket 10 Mbit va 100 Mbit bo'lishi
    mumkin va ular ekranda AYNAN bir xil ko'rinardi.

    Endi tezlik kartaning eng ko'rinadigan qismida, ulanish turi esa teg
    bilan: PPPoE paketini Hotspot abonenti sotib olishi ma'nosiz va buni
    xarid oldidan ko'rish kerak.
  */
  /*
    ⚠️ X-SIB-021 — KARTALAR BIR-BIRIGA TENGLASHMASDI.

    Uchta mustaqil sabab bir vaqtda ishlagan va ularning HAMMASINI
    tuzatish kerak — bittasi qolsa natija baribir qiyshiq bo'lardi:

    1. `Card` ga `height: 100%` berilgan edi, lekin `Row` ning standart
       hizalanishi `top` (`align-items: flex-start`), ya'ni `Col`
       qatorning balandligiga CHO'ZILMAYDI va `100%` hech narsaga
       nisbatan hisoblanmasdi. Yechim — `Row align="stretch"`
       (`PlansPage` da).

    2. `.ant-card-body` ning O'ZI flex konteyner emas edi. Ichkaridagi
       `height: 100%` li `Flex` tanaga cho'zilmaydi, demak
       `marginTop: 'auto'` (narx va tugma blokini pastga surish)
       HECH QACHON ishlamagan — tugmalar kontent qayerda tugasa,
       o'sha yerda turardi. Yechim — `styles.body` da flex ustun.

    3. Sarlavha uzunligi har xil: "10 GB" bir qator, "Uy interneti 100
       Mbit cheksiz" ikki qator. Yechim — `ellipsis` bilan qat'iy IKKI
       qator va to'liq nom `title` da (kesilgan matn sichqoncha ostida
       to'liq ko'rinadi). Uch qatorga chiqarish qatorlar sonini yana
       o'zgaruvchan qilardi.

    ⚠️ Tegga `flexShrink: 0` — usiz uzun sarlavha tegni siqib,
    "PPPoE" so'zi o'ralib ketardi.
  */
  return (
    <Card
      style={{ height: '100%' }}
      styles={{ body: { height: '100%', display: 'flex', flexDirection: 'column' } }}
    >
      <Flex vertical gap={10} style={{ flex: 1, minHeight: 0 }}>
        <Flex align="flex-start" justify="space-between" gap={8}>
          <Typography.Title
            level={4}
            style={{ margin: 0, flex: 1, minWidth: 0 }}
            ellipsis={{ rows: 2, tooltip: plan.displayName }}
          >
            {/*
              ⚠️ `displayName`, `name` EMAS. Kanonik nom operator uchun
              kalit (`tarif-11m`), abonent uchun esa ma'nosiz. Tarjima
              yo'q bo'lsa bu yerga baribir kanonik nom tushadi
              (`plansApi.ts`) — ya'ni bo'sh sarlavha bo'lishi mumkin
              emas, va "tarjima yo'q" degan ogohlantirish ham
              CHIQMAYDI: operator faqat bitta tilni to'ldirishi
              butunlay normal.
            */}
            {plan.displayName}
          </Typography.Title>
          {/* ⚠️ Rang — signalning O'ZI. Mos kelmagan paket kulrang tegda
              turganda abonent farqni umuman sezmasdi (`isMethodMismatch`). */}
          <Tag color={mismatch ? 'warning' : undefined} style={{ flexShrink: 0, marginInlineEnd: 0 }}>
            {t(`deliveryMethod.${plan.deliveryMethod}`)}
          </Tag>
        </Flex>

        {/* Teg yolg'iz yetmaydi: u FAKTNI aytadi ("Hotspot"), lekin nima
            uchun bu muhimligini aytmaydi. Matn shuni yopadi. */}
        {mismatch ? (
          <Typography.Text type="warning" style={{ fontSize: 12 }}>
            {t('plans.methodMismatch', { mine: t(`deliveryMethod.${subscriberKind}`) })}
          </Typography.Text>
        ) : null}

        {speed ? (
          <Flex align="center" gap={8}>
            <ThunderboltOutlined />
            <Typography.Text strong style={{ fontSize: 16 }}>
              {speed}
            </Typography.Text>
          </Flex>
        ) : (
          /*
            Tezlik ko'rsatilmagan — bu "cheksiz" DEGANI EMAS.

            ⚠️ VA U "hech narsa o'zgarmaydi" ham DEGANI EMAS. Bunday
            paketda siyosat yo'q, ya'ni u tezlik BERMAYDI. Agar abonentda
            tezlik beradigan boshqa amaldagi paket bo'lmasa, RADIUS
            ulanishni RAD ETADI ("Tarif biriktirilmagan",
            `DefaultRadiusAuthorizeService`) — abonent kabinetga kiradi,
            lekin internetga chiqa olmaydi.

            Bu aynan jonli tizimda sodir bo'lgan: `tarif-15m` paketi
            siyosatsiz, sotuvda va 0 narxda turgan. Ilgari bu yerda faqat
            "tezlik o'zgarmaydi" deb yozilardi va abonent nima uchun
            internetga chiqa olmayotganini bilmasdi.
          */
          <div>
            <Typography.Text type="secondary">{t('plans.speedUnchanged')}</Typography.Text>
            <Typography.Paragraph type="warning" style={{ margin: '4px 0 0', fontSize: 12 }}>
              {t('plans.speedUnchangedWarn')}
            </Typography.Paragraph>
          </div>
        )}

        <Descriptions
          size="small"
          column={1}
          colon={false}
          items={[
            { key: 'size', label: t('plans.size'), children: sizeLabel(t, plan) },
            { key: 'duration', label: t('plans.duration'), children: durationLabel(t, plan.validityDays) },
          ]}
        />

        {/*
          ⚠️ NARX VA TUGMA BIR QATORDA EMAS, TIK JOYLASHGAN.

          Ilgari ular yonma-yon turardi va narx qolgan joyga siqilardi:
          "350,000 afg'oni" telefon ekranida IKKI QATORGA bo'linib,
          "350,000" va "afg'oni" alohida tushardi. Narx — kartaning eng
          muhim raqami; u sinib ketsa abonent qancha to'lashini bir
          qarashda bilmaydi.

          Muammo faqat telefonda emas edi: 1440px da ham karta panjara
          ichida tor bo'lgani uchun bo'linardi.

          Tik joylashuvda narx to'liq bir qatorda turadi, tugma esa
          butun enda — telefonda barmoq bilan bosish ham osonroq.
        */}
        <Flex vertical gap={10} style={{ marginTop: 'auto' }}>
          <Typography.Title level={3} style={{ margin: 0, whiteSpace: 'nowrap' }}>
            {formatMoneyValue(plan.price)}
          </Typography.Title>
          <Button
            type="primary"
            block
            icon={<ShoppingOutlined />}
            loading={buying}
            onClick={onBuy}
          >
            {t('plans.buy')}
          </Button>
        </Flex>
      </Flex>
    </Card>
  )
}

export function PlansPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { message } = AntdApp.useApp()
  /*
    Abonentning O'Z ulanish turi — `isMethodMismatch` uchun. `user`
    bo'lmasligi mumkin emas (sahifa `RequireAuth` ostida), lekin
    `UNKNOWN` ga tushish ogohlantirishni JIMGINA o'chiradi, ya'ni
    noto'g'ri signal bermaydi.
  */
  const { user } = useAuth()
  const subscriberKind: DeliveryMethod = user?.kind ?? 'UNKNOWN'
  const [pendingPlan, setPendingPlan] = useState<Plan | null>(null)
  const [redeemOpen, setRedeemOpen] = useState(false)
  /*
    Bitta niyat = bitta kalit. Modal ochilganda emas, xarid TASDIQLANGANDA
    yaratiladi va oyna YOPILGUNCHA o'zgarmaydi.

    ⚠️ ILGARI u xatoda ham tashlanardi va bu PULGA tegadigan nuqson edi:
    tarmoq uzilishida yoki 5xx da so'rov serverda O'TIB KETGAN bo'lishi
    mumkin, panel esa xato ko'rsatardi. Abonent qayta bosardi, kalit
    YANGI bo'lardi va IKKINCHI paket sotib olinardi. Aynan shu hodisa
    boshqa sababdan bir marta sodir bo'lgan (`plansApi.ts` dagi
    `expiresAt` izohi).

    Endi kalit saqlanadi: o'sha oynadagi qayta urinish server uchun
    TAKRORIY so'rov bo'ladi va u eski natijani qaytaradi (`replay`).
    Yangi kalit faqat oyna yopilib, "Sotib olish" qaytadan bosilganda.
  */
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null)

  /*
    ⚠️ TIL SO'ROVGA QO'SHILADI VA KALITGA HAM.

    Paket nomi serverda tarjima qilinadi (`fetchPlans`), shuning uchun
    ikkalasi ham shart va ikkalasi ham AYNAN bir xil qiymatdan olinadi:

      · so'rovsiz — nom kanonik holida qolardi (XSB-007);
      · kalitsiz — til almashtirilganda kesh eski javobni qaytarardi va
        ekran o'zgarmasdi.

    `currentLanguage()` `i18n.language` dan ataylab afzal: u mintaqani
    tashlaydi (`uz-UZ` -> `uz`) va qo'llab-quvvatlanmagan qiymatni
    standartga tushiradi. Xom `i18n.language` bo'lsa har mintaqa uchun
    alohida kesh yozuvi paydo bo'lardi.
  */
  const lang = currentLanguage()
  const plansQuery = useQuery({
    queryKey: queryKeys.plans(lang),
    queryFn: () => fetchPlans(lang),
  })

  /*
    ⚠️ BALANS SHU EKRANDA — ilgari u faqat Bosh sahifada edi.

    Abonent "sotib olaman" tugmasini bosganda pul yetishini BILMASDI:
    tasdiq oynasi "Balansingizdan 50 000 so'm yechib olinadi" deb
    yozardi, balansning O'ZI esa boshqa sahifada qolardi. Ya'ni yagona
    tekshirish usuli — chiqib, qarab, qaytib kelish, yoki tugmani bosib
    ko'rish (va rad javobini olish).

    Kalit `queryKeys.balance` — Bosh sahifa bilan AYNAN bir xil, ya'ni
    ikkinchi so'rov yuborilmaydi (30 soniya `staleTime`) va xariddan
    keyingi `invalidateQueries` ikkala ekranni birdan yangilaydi.
  */
  const balanceQuery = useQuery({ queryKey: queryKeys.balance, queryFn: fetchBalance })
  /*
    `data !== undefined`, `isSuccess` EMAS — `DashboardPage` dagi bilan
    bir xil sabab: yiqilgan so'rov qo'ldagi eski, foydali sonni
    o'chirmasligi kerak, "balansingiz 0" degan yolg'onni esa umuman
    yozib bo'lmaydi.
  */
  const balance = balanceQuery.data?.money ?? null
  const refreshBalance = () => void balanceQuery.refetch()

  /*
    ⚠️ Xarid HAQIQATAN ikkilik (`plansApi.ts` dagi izoh): muvaffaqiyatsizlik
    bu yerga HECH QACHON `onSuccess` orqali kelmaydi — u har doim xato
    bo'lib keladi (masalan mablag' yetmasa 422). `onSuccess` faqat ikki
    holatni farqlaydi: yangi xarid (`replay: false`) va takroriy so'rov
    natijasi (`replay: true`).

    ⚠️ `onError` YO'Q — bu ataylab. Xato mutatsiyada QOLADI va oynaning
    ICHIDA ko'rsatiladi (`VoucherVoidModal` naqshi, admin panel).
    Ilgari u `message.error` bilan chiqib, oyna esa YOPILARDI: abonent
    tasdiq matnini ham, sababni ham yo'qotardi — xabar bir necha soniyada
    o'chadi va ekranda "bosdim, hech narsa bo'lmadi" qoladi.
  */
  const purchaseMutation = useMutation({
    mutationFn: purchasePlan,
    onSuccess: (result) => {
      setPendingPlan(null)
      setIdempotencyKey(null)
      void message.success(result.replay ? t('plans.purchaseReplay') : t('plans.purchaseOk'))
      void queryClient.invalidateQueries({ queryKey: queryKeys.balance })
      void queryClient.invalidateQueries({ queryKey: queryKeys.historyAll })
    },
  })

  const openConfirm = (plan: Plan) => {
    setPendingPlan(plan)
    setIdempotencyKey(newIdempotencyKey())
    // Oldingi xariddan qolgan xato yangi oynada chiqib qolmasin
    purchaseMutation.reset()
  }

  /** Oynani yopish — xato ham qoladi-ketadi, keyingi ochilish toza boshlanadi */
  const closeConfirm = () => {
    if (purchaseMutation.isPending) return
    setPendingPlan(null)
    setIdempotencyKey(null)
    purchaseMutation.reset()
  }

  const confirmPurchase = () => {
    if (!pendingPlan || !idempotencyKey) return
    purchaseMutation.mutate({ planId: pendingPlan.id, idempotencyKey })
  }

  /*
    ⚠️ RAD JAVOBI — XATO EMAS, VA BU FARQ EKRANDA KO'RINISHI KERAK.

    "Mablag' yetmadi" — serverning ANIQ javobi: hech narsa buzilmagan,
    pul ham yechilmagan. Uni qizil "xato" qutisida ko'rsatib, ostiga
    "qayta urinsangiz pul ikki marta yechilmaydi" deb yozish bu holatda
    ZARAR keltiradi: qayta urinish AYNAN shunday rad etiladi, chunki
    balans o'zgarmagan. Abonent bosaveradi va tizimni buzuq deb
    hisoblaydi.
  */
  const refused = purchaseMutation.isError && isInsufficientFunds(purchaseMutation.error)
  const shortfall = pendingPlan ? purchaseShortfall(pendingPlan.price, balance) : null
  /*
    Tugma FAQAT server RAD ETGANDAN KEYIN va biz yetishmasligini
    O'ZIMIZ ham ko'rib turganda o'chadi.

    ⚠️ Xariddan OLDIN o'chirilmaydi: balans keshdan kelgan bo'lishi
    mumkin (agent hozirgina to'ldirgan bo'lsa) va o'shanda tugmani
    o'chirish "sizga mumkin emas" degan YOLG'ON bo'lardi — haqiqiy
    qarorni server qabul qiladi (`isMethodMismatch` bilan bir xil
    qoida). Xariddan oldin faqat ogohlantiriladi.

    ⚠️ Balans noma'lum bo'lsa (`shortfall === null`) tugma OCHIQ
    qoladi: rad javobi eskirgan bo'lishi mumkin va yagona chorani
    tortib olish boshi berk ko'cha yasardi.
  */
  const blocked = refused && shortfall !== null

  return (
    <Flex vertical gap={16}>
      <Flex align="flex-start" justify="space-between" gap={12} wrap>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t('plans.title')}
          </Typography.Title>
          <Typography.Text type="secondary">{t('plans.subtitle')}</Typography.Text>
        </div>
        <Button icon={<QrcodeOutlined />} onClick={() => setRedeemOpen(true)}>
          {t('voucher.open')}
        </Button>
      </Flex>
      <VoucherRedeemModal open={redeemOpen} onClose={() => setRedeemOpen(false)} />

      {/*
        Balans NARXLAR BILAN BIR EKRANDA. Yonidagi tugma — abonent
        agentga to'lab kelgach shu yerda tekshirishi uchun: usiz yagona
        yo'l sahifani F5 bilan yangilash bo'lardi.
      */}
      <Card size="small">
        <Flex align="center" justify="space-between" gap={12} wrap>
          <Flex align="center" gap={8}>
            <WalletOutlined />
            <Typography.Text type="secondary">{t('plans.yourBalance')}</Typography.Text>
            {balanceQuery.isPending ? (
              <Skeleton.Button active size="small" style={{ width: 120 }} />
            ) : (
              <Typography.Text strong style={{ fontSize: 18 }}>
                {/* O'qilmagan balans — chiziqcha, "0 so'm" EMAS */}
                {balance ? formatMoneyValue(balance) : t('common.dash')}
              </Typography.Text>
            )}
          </Flex>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={balanceQuery.isFetching}
            onClick={refreshBalance}
          >
            {t('plans.refreshBalance')}
          </Button>
        </Flex>
        {/* Sabab aytiladi: bo'sh chiziqcha o'zi "balansingiz yo'q" bo'lib o'qiladi */}
        {balanceQuery.isError ? (
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
            {t('plans.balanceUnknown')}
          </Typography.Text>
        ) : null}
      </Card>

      {plansQuery.isPending ? (
        <Row gutter={[16, 16]}>
          {[1, 2, 3].map((i) => (
            <Col xs={24} sm={12} lg={8} xxl={6} key={i}>
              <Card><Skeleton active paragraph={{ rows: 3 }} /></Card>
            </Col>
          ))}
        </Row>
      ) : plansQuery.isError ? (
        <LoadError
          titleKey="plans.loadFailed"
          error={plansQuery.error}
          onRetry={() => void plansQuery.refetch()}
        />
      ) : plansQuery.data.length === 0 ? (
        <EmptyState icon={<ShoppingOutlined />} title={t('plans.empty')} />
      ) : (
        // ⚠️ `align="stretch"` — `Row` ning standarti `top`, ya'ni ustunlar
        // qator balandligiga cho'zilmaydi va kartadagi `height: 100%`
        // hech narsa qilmaydi (`PlanCard` izohidagi 1-sabab).
        <Row gutter={[16, 16]} align="stretch">
          {plansQuery.data.map((plan) => (
            <Col xs={24} sm={12} lg={8} xxl={6} key={plan.id}>
              <PlanCard
                plan={plan}
                subscriberKind={subscriberKind}
                buying={purchaseMutation.isPending && pendingPlan?.id === plan.id}
                onBuy={() => openConfirm(plan)}
              />
            </Col>
          ))}
        </Row>
      )}

      <Modal
        open={pendingPlan !== null}
        /* Tasdiq oynasi ham ekran — bu yerda ham `displayName`. Ikkalasi
           ayrilsa abonent kartada bir nom, oynada boshqa nom ko'rardi. */
        title={pendingPlan ? t('plans.confirmTitle', { name: pendingPlan.displayName }) : ''}
        onCancel={closeConfirm}
        onOk={confirmPurchase}
        okText={purchaseMutation.isPending ? t('plans.buying') : t('plans.buy')}
        confirmLoading={purchaseMutation.isPending}
        okButtonProps={{ disabled: blocked }}
        cancelButtonProps={{ disabled: purchaseMutation.isPending }}
      >
        <Flex vertical gap={12}>
          {pendingPlan && (
            <Typography.Text>
              {t('plans.confirmText', {
                price: formatMoneyValue(pendingPlan.price),
                size: sizeLabel(t, pendingPlan),
                duration: durationLabel(t, pendingPlan.validityDays),
              })}
            </Typography.Text>
          )}

          {/*
            Narx yozilgan joyda balans ham yozilishi kerak: pul aynan shu
            tugmada yechiladi va "yetadimi?" degan savolga javob boshqa
            ekranda qolmasin.
          */}
          <Typography.Text type="secondary">
            {t('plans.balanceNow', {
              balance: balance ? formatMoneyValue(balance) : t('common.dash'),
            })}
          </Typography.Text>

          {/*
            ⚠️ OGOHLANTIRISH OYNADA HAM TAKRORLANADI — bu ortiqcha emas.

            Karta va oyna — ikki BOSHQA lahza: kartada abonent tanlayapti,
            oynada esa PUL yechilishini tasdiqlayapti. Tasdiq oynasi
            kartani to'sib turadi, ya'ni kartadagi sariq qator o'sha
            paytda ko'rinmaydi. Oxirgi to'xtash nuqtasida ogohlantirish
            bo'lmasa, u umuman bo'lmaganday bo'lardi.
          */}
          {pendingPlan && isMethodMismatch(pendingPlan, subscriberKind) ? (
            <Alert
              type="warning"
              showIcon
              message={t('plans.methodMismatch', {
                mine: t(`deliveryMethod.${subscriberKind}`),
              })}
            />
          ) : null}

          {/*
            Pul yetmasligi XARIDDAN OLDIN aytiladi — tugma bosilgunga
            qadar. Ogohlantirish, to'siq emas: yuqoridagi `blocked`
            izohiga qarang.
          */}
          {shortfall && !refused ? (
            <Alert
              type="warning"
              showIcon
              message={t('plans.notEnough', { amount: formatMoneyValue(shortfall) })}
              description={t('plans.topUpHint')}
            />
          ) : null}

          {/*
            Xato shu yerda — tugmaning YONIDA. Sababi va keyingi qadam
            bir ekranda turadi.

            ⚠️ IKKI BOSHQA HOLAT, IKKI BOSHQA QUTI:

            · RAD JAVOBI (mablag' yetmadi) — sariq, chunki hech narsa
              buzilmagan. Keyingi qadam — balansni to'ldirish, qayta
              urinish EMAS: pul o'zgarmagunicha javob o'sha-o'sha
              bo'ladi. Shuning uchun bu shoxda `plans.retrySafe`
              KO'RSATILMAYDI va uning o'rnida haqiqiy chiqish yo'li
              turadi.

            · XATO (tarmoq, 5xx) — qizil. Bu yerda esa qayta urinish
              AYNAN to'g'ri maslahat va u xavfsiz: `idempotencyKey` oyna
              umriga bog'langan, ya'ni ikkinchi so'rov server uchun
              TAKROR bo'ladi va pul ikki marta yechilmaydi.
          */}
          {purchaseMutation.isError ? (
            refused ? (
              <Alert
                type="warning"
                showIcon
                message={
                  shortfall
                    ? t('plans.notEnough', { amount: formatMoneyValue(shortfall) })
                    : t('plans.notEnoughUnknown')
                }
                description={t('plans.topUpHint')}
                action={
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={balanceQuery.isFetching}
                    onClick={refreshBalance}
                  >
                    {t('plans.refreshBalance')}
                  </Button>
                }
              />
            ) : (
              <Alert
                type="error"
                showIcon
                message={translateError(purchaseMutation.error, t)}
                description={t('plans.retrySafe')}
              />
            )
          ) : null}
        </Flex>
      </Modal>
    </Flex>
  )
}
